import moment from 'moment';

import { IRenderFormFieldProps } from '@/components';
import { DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT } from '@/config';
import { GenderList, RaceList } from '@/constants/form_inputs';
import {
  AttemptIndicator,
  CFSEvent,
  DispositionReasons,
  IUser,
  KaseTypes,
  MasterNameType,
  NarrativeTypes,
  OrganizationType,
  PhoneNumberType,
} from '@/models';
import {
  formatNarrativeContent,
  formatPersonName,
  formatPhoneNumber,
  titleCase,
} from '@/utils';
import {
  formatCityStateZip,
  formatStreetLine,
  getActiveAddress,
} from '@/utils/printAddress';

const multiEmailPattern =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(,\s*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/;

const getActiveLicensePlateValue = (
  data: Record<string, any> | null | undefined,
  field?: string,
) => {
  const plates = Array.isArray(data?.licensePlates) ? data.licensePlates : [];
  const activePlate =
    plates.find((plate: any) => plate?.isActive) ||
    (plates.length === 1 ? plates[0] : undefined) ||
    data?.licensePlate;

  return activePlate?.[field as string]?.toString() ?? '';
};

export interface IKasePrintSettingsType {
  pageOptions: {
    printHeaderOnAllPages: IRenderFormFieldProps;
    includeSensitiveInfo: IRenderFormFieldProps;
  };
  emailDetails: {
    emailAddress: IRenderFormFieldProps;
    emailBody: IRenderFormFieldProps;
  };
  auditDetails: {
    recipient: IRenderFormFieldProps;
    reason: IRenderFormFieldProps;
  };
}

export const KasePrintSettings: IKasePrintSettingsType = {
  pageOptions: {
    printHeaderOnAllPages: {
      name: 'printHeaderOnAllPages',
      type: 'header-position-toggle',
      label: 'Print header on:',
    },
    includeSensitiveInfo: {
      name: 'includeSensitiveInfo',
      type: 'checkbox',
      label: 'Include Sensitive Information',
      color: 'error',
    },
  },
  emailDetails: {
    emailAddress: {
      name: 'emailAddress',
      label: 'Recipient Email Address',
      rules: {
        required: 'Please enter at least one valid email address.',
        pattern: {
          value: multiEmailPattern,
          message: 'All entered email address(es) must be valid.',
        },
      },
    },
    emailBody: {
      name: 'emailBody',
      type: 'text-area',
      label: 'Email Body',
      multiline: true,
      rows: 5,
      rules: {
        maxLength: {
          value: 320,
          message: 'Email Body should be less than 320 characters.',
        },
      },
    },
  },
  auditDetails: {
    recipient: {
      name: 'recipient',
      type: 'text',
      label: 'Recipient',
      rules: {
        maxLength: {
          value: 80,
          message: 'Recipent detail should be less than 80 characters.',
        },
      },
    },
    reason: {
      name: 'reason',
      type: 'text-area',
      label: 'Reason',
      multiline: true,
      rows: 5,
      rules: {
        maxLength: {
          value: 270,
          message: 'Reason should be less than 270 characters.',
        },
      },
    },
  },
};

export interface IKasePrintModalForm {
  printHeaderOnAllPages: boolean;
  includeSensitiveInfo: boolean;
  emailAddress: string;
  emailBody: string;
  recipient: string;
  reason: string;
}

export enum KasePrintSections {
  CaseOverview = 'caseOverview',
  DispatchInfo = 'dispatchInfo',
  Officers = 'officers',
  OffenseInfo = 'offenseInfo',
  NamesList = 'names',
  VehiclesList = 'vehicles',
  NarrativesList = 'narratives',
  PropertyList = 'property',
  EmailDetails = 'emailDetails',
  AuditDetails = 'auditDetails',
}

export const KasePrintFieldSections = [
  {
    label: 'Main Case Information',
    value: 'caseOverview',
    multipleData: false,
  },
  {
    label: 'Dispatch Information',
    value: 'dispatchInfo',
    multipleData: false,
  },
  {
    label: 'Officers Involved',
    value: 'officers',
    multipleData: true,
  },
  {
    label: 'Offenses',
    value: 'offenseInfo',
    multipleData: true,
  },
  {
    label: 'Names',
    value: 'names',
    multipleData: true,
  },
  {
    label: 'Vehicles',
    value: 'vehicles',
    multipleData: true,
  },
  {
    label: 'Narratives',
    value: 'narratives',
    multipleData: true,
  },
  {
    label: 'Property Involved',
    value: 'property',
    multipleData: true,
  },
  {
    label: 'Email Details',
    value: 'emailDetails',
    multipleData: false,
  },
  {
    label: 'Audit Details',
    value: 'auditDetails',
    multipleData: false,
  },
];

export interface IKasePrintFields {
  label: string;
  value: string;
  type?: string;
  objectField?: string;
  redacted: true | false;
  redactedWords?: Set<string>;
  originalContent?: string;
}

export const KasePrintFields: Record<string, IKasePrintFields[]> = {
  caseOverview: [
    { label: 'Case ID number', value: 'number', type: 'text', redacted: false },
    { label: 'Status', value: 'status', type: 'text', redacted: false },
    {
      label: 'Primary officer',
      value: 'primaryOfficer',
      type: 'object',
      objectField: 'fullName',
      redacted: false,
    },
    {
      label: 'Primary investigator',
      value: 'primaryInvestigator',
      type: 'object',
      objectField: 'fullName',
      redacted: false,
    },
    { label: 'Case type', value: 'type', type: 'text', redacted: false },
    {
      label: 'Incident date and time',
      value: 'incidentStartedAt',
      type: 'date_time',
      redacted: false,
    },
    {
      label: 'Report date and time',
      value: 'incidentReportedAt',
      type: 'date_time',
      redacted: false,
    },
    {
      label: 'Address',
      value: 'address',
      type: 'object',
      objectField: 'normalized',
      redacted: false,
    },
    {
      label: 'Location code of Incident',
      value: 'locationCode',
      type: 'object',
      objectField: 'code',
      redacted: false,
    },
    {
      label: 'State/Division/Precint Identifier',
      value: 'sdpIdentifier',
      type: 'text',
      redacted: false,
    },
    {
      label: 'Disposition',
      value: 'currentDisposition',
      type: 'object',
      objectField: 'reason',
      redacted: false,
    },
    {
      label: 'Disposition date and time',
      value: 'currentDisposition',
      type: 'object',
      objectField: 'createdAt',
      redacted: false,
    },
    {
      label: 'Assisting Officers',
      value: 'assistingOfficers',
      type: 'array',
      objectField: 'fullName',
      redacted: false,
    },
  ],
  dispatchInfo: [
    {
      label: 'CFS ID',
      value: 'number',
      type: 'text',
      redacted: false,
    },
    {
      label: 'Address',
      value: 'addressInfo',
      type: 'object',
      objectField: 'normalized',
      redacted: false,
    },
    {
      label: 'Incident codes',
      value: 'incidentCodes',
      type: 'array',
      objectField: 'code',
      redacted: false,
    },
    {
      label: 'Description',
      value: 'shortDescription',
      type: 'text',
      redacted: false,
    },
    {
      label: 'Call dispatched',
      value: 'wasReadyToDispatchAt',
      type: 'date_time',
      redacted: false,
    },
    {
      label: 'Start',
      value: 'start',
      type: 'date_time',
      redacted: false,
    },
    {
      label: 'End',
      value: 'end',
      type: 'date_time',
      redacted: false,
    },
    {
      label: 'Assigned',
      value: 'cfsHistoryLogs',
      type: 'array',
      objectField: 'assigned',
      redacted: false,
    },
    {
      label: 'En route',
      value: 'cfsHistoryLogs',
      type: 'array',
      objectField: 'en_route',
      redacted: false,
    },
    {
      label: 'On scene',
      value: 'cfsHistoryLogs',
      type: 'array',
      objectField: 'on_scene',
      redacted: false,
    },
    {
      label: 'CFS Closed Time',
      value: 'cfsHistoryLogs',
      type: 'array',
      objectField: 'closed',
      redacted: false,
    },
  ],
  officers: [
    { label: 'Rank', value: 'rank', type: 'text', redacted: false },
    { label: 'Name', value: 'name', type: 'text', redacted: false },
    { label: 'Role', value: 'role', type: 'text', redacted: false },
    { label: 'Badge Number', value: 'badge', type: 'text', redacted: false },
  ],
  offenseInfo: [
    {
      label: 'Offense ID number',
      value: 'number',
      type: 'text',
      redacted: false,
    },
    {
      label: 'Attempt',
      value: 'attemptIndicator',
      type: 'text',
      redacted: false,
    },
    {
      label: 'Law description',
      value: 'penalCode',
      type: 'object',
      objectField: 'lawDescription',
      redacted: false,
    },
    {
      label: 'Penal code',
      value: 'penalCode',
      type: 'object',
      objectField: 'code',
      redacted: false,
    },
    {
      label: 'NIBRS code',
      value: 'penalCode',
      objectField: 'ibrCode',
      type: 'object',
      redacted: false,
    },
  ],
  names: [
    {
      value: 'name',
      label: 'Name',
      type: 'object',
      objectField: 'fullName',
      redacted: false,
    },
    {
      value: 'involvement',
      label: 'Role',
      type: 'text',
      redacted: false,
    },
    { value: 'sex', label: 'Sex', type: 'text', redacted: false },
    { value: 'race', label: 'Race', type: 'text', redacted: false },
    {
      value: 'birthday',
      label: 'Date of birth',
      type: 'date',
      redacted: false,
    },
    { value: 'age', label: 'Age', type: 'text', redacted: false },
    {
      value: 'addresses',
      label: 'Address',
      type: 'object',
      objectField: 'addressLabel',
      redacted: false,
    },
    {
      value: 'addresses',
      label: 'Sub address',
      type: 'object',
      objectField: 'subAddress',
      redacted: false,
    },
    {
      value: 'cityStateZip',
      label: 'City / State / ZIP',
      type: 'text',
      redacted: false,
    },
    {
      value: 'organizationType',
      label: 'Organization type',
      type: 'text',
      redacted: false,
    },
    { value: 'phone', label: 'Phone', type: 'text', redacted: false },
    {
      value: 'businessPhone',
      label: 'Business phone',
      type: 'text',
      redacted: false,
    },
    {
      value: 'mobilePhone',
      label: 'Mobile phone',
      type: 'text',
      redacted: false,
    },
    { value: 'type', label: 'Type', type: 'text', redacted: false },
    // { value: 'offenses', label: 'Offenses', type: 'array', objectField: 'number', redacted: false },
  ],
  vehicles: [
    { value: 'year', label: 'Year', type: 'text', redacted: false },
    { value: 'color', label: 'Color', type: 'text', redacted: false },
    { value: 'make', label: 'Make', type: 'text', redacted: false },
    { value: 'model', label: 'Model', type: 'text', redacted: false },
    {
      value: 'licensePlate',
      label: 'Plate',
      type: 'object',
      objectField: 'number',
      redacted: false,
    },
    {
      value: 'owner',
      label: 'Owner',
      type: 'object',
      objectField: 'fullName',
      redacted: false,
    },
  ],
  property: [
    { value: 'name', label: 'Name', type: 'text', redacted: false },
    { value: 'involvement', label: 'Role', type: 'text', redacted: false },
    { value: 'typeTag', label: 'Type', type: 'text', redacted: false },
    { value: 'type', label: 'Item', type: 'text', redacted: false },
    { value: 'number', label: 'Item#', type: 'text', redacted: false },
    { value: 'serialNumber', label: 'Serial#', type: 'text', redacted: false },
    { value: 'value', label: 'Value', type: 'text', redacted: false },
    {
      value: 'owner',
      label: 'Owner',
      type: 'object',
      objectField: 'fullName',
      redacted: false,
    },
    { value: 'status', label: 'Status', type: 'text', redacted: false },
    {
      value: 'loggedInfo',
      label: 'Collected by',
      type: 'object',
      objectField: 'loggedBy',
      redacted: false,
    },
    {
      value: 'loggedInfo',
      label: 'Date logged',
      type: 'object',
      objectField: 'loggedDate',
      redacted: false,
    },
    {
      value: 'droppedOffLocation',
      label: 'Location',
      type: 'object',
      objectField: 'name',
      redacted: false,
    },
    { value: 'year', label: 'Year', type: 'text', redacted: false },
    { value: 'make', label: 'Make', type: 'text', redacted: false },
    { value: 'model', label: 'Model', type: 'text', redacted: false },
    {
      value: 'licensePlate',
      label: 'Plate',
      type: 'object',
      objectField: 'number',
      redacted: false,
    },
    { value: 'color', label: 'Color', type: 'text', redacted: false },
  ],
  narratives: [
    { value: 'title', label: 'Title', type: 'text', redacted: false },
    {
      value: 'createdBy',
      label: 'Author',
      type: 'object',
      objectField: 'fullName',
      redacted: false,
    },
    {
      value: 'createdAt',
      label: 'Authored on',
      type: 'date_time',
      redacted: false,
    },
    { value: 'type', label: 'Type', type: 'text', redacted: false },
    {
      value: 'content',
      label: 'Narrative',
      type: 'object',
      objectField: 'text',
      redacted: false,
    },
  ],
  emailDetails: [
    {
      value: 'emailAddress',
      label: 'Recipient Email Address',
      type: 'text',
      redacted: false,
    },
    {
      value: 'emailBody',
      label: 'Email Body',
      type: 'text',
      redacted: false,
    },
  ],
  auditDetails: [
    {
      value: 'recipient',
      label: 'Recipient',
      type: 'text',
      redacted: false,
    },
    {
      value: 'reason',
      label: 'Reason',
      type: 'text',
      redacted: false,
    },
  ],
};

export const renderKasePrintInfoContent = (
  sectionName: string,
  sectionField: IKasePrintFields,
  data?: Record<string, any> | null,
  PropertyType?: Map<string, { key?: string; label?: string }>,
  PropertyInvolvementList?: Map<string, { key?: string; label?: string }>,
): string => {
  let output = '';

  switch (sectionField.type) {
    case 'date':
      output = data?.[sectionField?.value]
        ? moment(data[sectionField.value]).format(DATE_FORMAT)
        : '';
      break;
    case 'date_time':
      output = data?.[sectionField?.value]
        ? moment(data[sectionField.value]).format(DATE_TIME_FORMAT)
        : '';
      break;
    case 'time':
      output = data?.[sectionField?.value]
        ? moment(data[sectionField.value]).format(TIME_FORMAT)
        : '';
      break;
    case 'object':
      if (
        sectionField.objectField === 'fullName' &&
        sectionField.value !== 'primaryOfficer' &&
        sectionField.value !== 'primaryInvestigator'
      ) {
        const suffix =
          data?.owner?.suffix ?? data?.createdBy?.suffix ?? data?.suffix ?? '';
        const firstName =
          data?.owner?.firstName ??
          data?.createdBy?.firstName ??
          data?.firstName ??
          '';
        const middleName =
          data?.owner?.middleName ??
          data?.createdBy?.middleName ??
          data?.middleName ??
          '';
        const lastName =
          data?.owner?.lastName ??
          data?.createdBy?.lastName ??
          data?.lastName ??
          '';
        if (data?.type === MasterNameType.ORGANIZATION) {
          output = data?.name ?? '';
          break;
        }

        output = formatPersonName(
          { firstName, middleName, lastName, suffix },
          { includeSuffix: true },
        );
        break;
      }

      if (
        sectionField.objectField === 'fullName' &&
        (sectionField.value === 'primaryOfficer' ||
          sectionField.value === 'primaryInvestigator')
      ) {
        const suffix =
          data?.[sectionField.value]?.suffix ??
          data?.createdBy?.suffix ??
          data?.suffix ??
          '';
        const firstName = data?.[sectionField.value]?.firstName ?? '';
        const middleName = data?.[sectionField.value]?.middleName ?? '';
        const lastName = data?.[sectionField.value]?.lastName ?? '';

        const officerName = formatPersonName(
          { firstName, middleName, lastName, suffix },
          { includeSuffix: true },
        );
        output = officerName ? `${officerName} ` : '';

        output += `${
          data?.[sectionField.value]?.profile?.employmentInformation
            ?.badgeNumber
            ? `[ Badge: ${
                data?.[sectionField.value]?.profile?.employmentInformation
                  ?.badgeNumber
              } ]`
            : ''
        }`;
        break;
      }

      if (sectionField.objectField === 'normalized') {
        const addr =
          sectionField.value === 'addressInfo'
            ? data?.addressInfo?.address
            : data?.[sectionField.value];
        if (!addr) {
          output = '';
          break;
        }
        const street = formatStreetLine(addr);
        const cityStateZip = formatCityStateZip(addr);
        output = [street, cityStateZip].filter(Boolean).join(', ');
        break;
      }

      if (sectionField.value === 'licensePlate') {
        output = getActiveLicensePlateValue(data, sectionField.objectField);
        break;
      }

      if (sectionField.value === 'locationCode') {
        output = data?.locationCode
          ? `${data.locationCode?.code} ${data.locationCode?.locationLiteral} - ${data.locationCode?.geographicType}`
          : '';
        break;
      }

      if (
        sectionField.value === 'penalCode' &&
        sectionField.objectField === 'code'
      ) {
        output = data?.penalCode
          ? `${data.penalCode?.lawTitle ?? ' - '} ${
              data.penalCode?.section ?? ' - '
            } ` +
            `${data.penalCode?.subsection ?? ' - '} ${
              data.penalCode?.statutoryClass ?? ' - '
            } ` +
            `${data.penalCode?.category ?? ' - '} ${
              data.penalCode?.degree ?? ' - '
            } ` +
            `${data?.attemptIndicator ?? ' - '}`
          : '';
        break;
      }

      if (sectionField.objectField === 'text') {
        output =
          data?.content && formatNarrativeContent(data?.content?.text?.blocks);
        break;
      }

      // Property-specific field handling
      if (sectionField.value === 'loggedInfo') {
        if (sectionField.objectField === 'loggedBy') {
          const loggedBy = data?.loggedInfo?.loggedBy;
          if (loggedBy) {
            output = formatPersonName(loggedBy);
          }
          break;
        }
        if (sectionField.objectField === 'loggedDate') {
          output = data?.loggedInfo?.loggedDate
            ? moment(data.loggedInfo.loggedDate).format(DATE_TIME_FORMAT)
            : '';
          break;
        }
      }

      // Handle addresses array - prefer ACTIVE address, fall back to first or legacy
      if (sectionField.value === 'addresses') {
        if ((data as any)?.transient) {
          output =
            sectionField.objectField === 'addressLabel' ||
            sectionField.objectField === 'normalized'
              ? 'Transient'
              : '';
          break;
        }
        const primaryAddress = getActiveAddress(data as any);
        if (!primaryAddress) {
          output = '';
          break;
        }
        if (sectionField.objectField === 'addressLabel') {
          output = formatStreetLine(primaryAddress);
        } else if (sectionField.objectField === 'normalized') {
          const street = formatStreetLine(primaryAddress);
          const cityStateZip = formatCityStateZip(primaryAddress);
          output = [street, cityStateZip].filter(Boolean).join(', ');
        } else {
          output =
            (primaryAddress as any)?.[sectionField.objectField as string] || '';
        }
        break;
      }

      if (
        sectionField.value === 'currentDisposition' &&
        sectionField.objectField === 'reason'
      ) {
        const reason = data?.currentDisposition?.reason ?? '';
        output = `${
          reason
            ? DispositionReasons.find((item) => item.value === reason)?.label ??
              ''
            : ''
        }`;
        break;
      }

      if (
        sectionField.value === 'currentDisposition' &&
        sectionField.objectField === 'createdAt'
      ) {
        const dateData = data?.currentDisposition?.createdAt;
        output = dateData ? moment(dateData)?.format(DATE_TIME_FORMAT) : '';
        break;
      }

      if (data?.[sectionField.value]?.[sectionField.objectField as string]) {
        output =
          data?.[sectionField.value]?.[sectionField.objectField as string];
        break;
      }
      break;

    case 'array': {
      const dataLen: number = data?.[sectionField.value]?.length ?? 1;

      if (sectionField.value === 'incidentCodes') {
        output = data?.incidentCodes
          ? data.incidentCodes
              ?.map(
                (iCode: { _id: string; code?: string; description?: string }) =>
                  `${iCode?.code ?? ''} - ${iCode?.description ?? ''}`,
              )
              ?.join(', ')
          : '';
        break;
      }

      if (sectionField.value === 'assistingOfficers') {
        output =
          data?.assistingOfficers
            ?.map(
              (officer: IUser) =>
                `${officer?.fullName} [ Badge: ${officer?.profile?.employmentInformation?.badgeNumber} ]`,
            )
            ?.join(', ') ?? '';
        break;
      }

      if (
        sectionField.value === 'cfsHistoryLogs' &&
        data?.cfsHistoryLogs?.length
      ) {
        if (sectionField.objectField === 'assigned') {
          output = getCFSAssignedUnitStatusDate(
            data?.cfsHistoryLogs,
            CFSEvent.UNIT_SHIFT_ASSIGNED,
          );
          break;
        }

        if (sectionField.objectField === 'en_route') {
          output = getCFSAssignedUnitStatusDate(
            data?.cfsHistoryLogs,
            CFSEvent.UNIT_SHIFT_IS_EN_ROUTE,
          );
          break;
        }

        if (sectionField.objectField === 'on_scene') {
          output = getCFSAssignedUnitStatusDate(
            data?.cfsHistoryLogs,
            CFSEvent.UNIT_SHIFT_IS_ON_SCENE,
          );
          break;
        }

        if (sectionField.objectField === 'closed' && !!data?.isClosed) {
          output = getCFSAssignedUnitStatusDate(
            data?.cfsHistoryLogs,
            CFSEvent.CLOSED,
          );
          break;
        }
      }

      if (
        data?.[sectionField.value]?.[0]?.[sectionField.objectField as string]
      ) {
        output =
          data?.[sectionField.value]?.[dataLen - 1]?.[
            sectionField.objectField as string
          ];
        break;
      }
      break;
    }
    case 'text':
    default:
      output = data?.[sectionField.value] ?? '';
      if (sectionField.value === 'cityStateZip') {
        if ((data as any)?.transient) {
          output = '';
          break;
        }
        const addr = getActiveAddress(data as any);
        output = addr ? formatCityStateZip(addr) : '';
        break;
      }

      if (sectionField.value === 'phone') {
        output = formatPhoneNumber(data?.phoneNumbers?.[0]?.phoneNumber ?? '');
        break;
      }

      if (sectionField.value === 'businessPhone') {
        const businessPhone = data?.phoneNumbers?.find(
          (p: any) => p.type === PhoneNumberType.WORK,
        );
        output = formatPhoneNumber(businessPhone?.phoneNumber ?? '');
        break;
      }

      if (sectionField.value === 'mobilePhone') {
        const mobilePhone = data?.phoneNumbers?.find(
          (p: any) => p.type === PhoneNumberType.MOBILE,
        );
        output = formatPhoneNumber(mobilePhone?.phoneNumber ?? '');
        break;
      }

      if (sectionField.value === 'organizationType') {
        const orgType = data?.organizationType as keyof typeof OrganizationType;
        output = orgType ? OrganizationType[orgType] ?? orgType : '';
        break;
      }

      if (sectionField.value === 'sex') {
        output = data?.sex
          ? GenderList[data?.sex as keyof typeof GenderList]
          : '';
        break;
      }

      if (sectionField.value === 'race') {
        output = data?.race
          ? RaceList[data?.race as keyof typeof RaceList]
          : '';
        break;
      }

      if (sectionField.value === 'age') {
        if (data?.birthday) {
          const birthDate = moment(data.birthday);
          const age = moment().diff(birthDate, 'years');
          output = age.toString();
        } else {
          output = '';
        }
        break;
      }

      if (sectionField.value === 'attemptIndicator') {
        output = data?.attemptIndicator
          ? data.attemptIndicator === AttemptIndicator.ATTEMPTED
            ? 'Attempted'
            : data.attemptIndicator === AttemptIndicator.COMPLETED
            ? 'Completed'
            : data.attemptIndicator
          : '';
        break;
      }

      if (sectionField.value === 'involvement') {
        // property involvement/role
        if (
          sectionName === KasePrintSections.PropertyList &&
          PropertyInvolvementList &&
          PropertyInvolvementList instanceof Map
        ) {
          output =
            PropertyInvolvementList.get(data?.involvement)?.label ??
            data?.involvement;
          break;
        }
      }

      if (sectionField.value === 'type' && data?.type) {
        output = titleCase(data?.type?.toString());

        // case type
        if (sectionName === KasePrintSections.CaseOverview) {
          output = KaseTypes[data.type as keyof typeof KaseTypes];
          break;
        }

        // name type
        if (sectionName === KasePrintSections.NamesList) {
          output = MasterNameType[data.type as keyof typeof MasterNameType];
          break;
        }

        // vehicle type
        if (sectionName === KasePrintSections.VehiclesList) {
          output = data.type;
          break;
        }

        // narrative type
        if (sectionName === KasePrintSections.NarrativesList) {
          output =
            NarrativeTypes[data.type as keyof typeof NarrativeTypes] ??
            data.type;
          break;
        }

        // property type
        if (
          sectionName === KasePrintSections.PropertyList &&
          PropertyType &&
          PropertyType instanceof Map
        ) {
          output =
            [...PropertyType.values()].find((val) => val.key === data?.type)
              ?.label ?? '';
          break;
        }
      }
  }

  return String(output)?.trim();
};

type CFSHistoryLogForPrint = {
  event: CFSEvent;
  createdAt?: string | Date;
  overriddenAt?: number | null;
};

const getEffectiveLogDate = (
  log: CFSHistoryLogForPrint,
): number | string | Date | null => {
  if (log.overriddenAt === null) return null;
  if (typeof log.overriddenAt === 'number' && !Number.isNaN(log.overriddenAt)) {
    return log.overriddenAt;
  }
  return log.createdAt ?? null;
};

export const getCFSEventEffectiveDate = (
  unitShifts: CFSHistoryLogForPrint[] | undefined,
  event: CFSEvent,
): number | string | Date | null => {
  const matching = (unitShifts || [])
    .filter((unit) => unit.event === event)
    .map((unit) => getEffectiveLogDate(unit))
    .filter((value): value is number | string | Date => value !== null);

  if (matching.length === 0) return null;

  return matching.sort((a, b) => moment(b).valueOf() - moment(a).valueOf())[0];
};

const getCFSAssignedUnitStatusDate = (
  unitShifts: CFSHistoryLogForPrint[] | undefined,
  event: CFSEvent,
) => {
  const effectiveDate = getCFSEventEffectiveDate(unitShifts, event);

  return effectiveDate
    ? moment(effectiveDate)?.format(DATE_TIME_FORMAT) ?? ''
    : '';
};
